// The hosted page's link, with a one-time option to swap the generated
// 4-character code for the installer's own (e.g. go.xenquote.com/smith-heating).
// Typing checks availability as you go; saving asks for confirmation first,
// because the choice is locked and can't be undone or changed later.
// The old code keeps opening the same page, so links and QR codes already
// shared still work.
import { useEffect, useState } from "react";
import { Check, Lock, PencilLine, X } from "lucide-react";
import { useApi, type Widget } from "../api";
import { ConfirmDialog } from "./ConfirmDialog";

// Same rule as the API: 3-40 lowercase letters, numbers and single hyphens,
// starting and ending with a letter or number
const LINK_RE = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){2,39}$/;

type LinkCheck = { state: "idle" | "checking" | "ok" | "bad"; reason?: string };

/** Lower-case, spaces to hyphens, drop anything else a link can't hold. */
function tidy(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40);
}

export function HostedLinkEditor({ widget, onSaved }: { widget: Widget; onSaved: (w: Widget) => void }) {
  const api = useApi();
  const url = widget.hostedUrl ?? "";
  const code = widget.hostedCode ?? "";
  // Everything before the link, e.g. https://go.dev.xenquote.com/
  const base = url.slice(0, url.length - code.length);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [check, setCheck] = useState<LinkCheck>({ state: "idle" });
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check availability shortly after typing stops (format first, locally)
  useEffect(() => {
    if (!editing || !value) return setCheck({ state: "idle" });
    if (value === code) return setCheck({ state: "bad", reason: "That's the link you have now." });
    if (!LINK_RE.test(value)) {
      return setCheck({ state: "bad", reason: "Use 3-40 lowercase letters, numbers and single hyphens, starting and ending with a letter or number." });
    }
    setCheck({ state: "checking" });
    let stale = false;
    const t = window.setTimeout(() => {
      api
        .checkHostedLink(value)
        .then((r) => !stale && setCheck(r.available ? { state: "ok" } : { state: "bad", reason: r.reason }))
        .catch((e) => !stale && setCheck({ state: "bad", reason: (e as Error).message }));
    }, 400);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [api, editing, value, code]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      onSaved(await api.setHostedLink(widget.widgetId, value));
      setEditing(false);
      setConfirming(false);
      setValue("");
    } catch (e) {
      setError((e as Error).message);
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  }

  // Chosen already: show it as fixed
  if (widget.hostedCodeLocked) {
    return (
      <p className="hosted-link-note">
        <Lock size={13} aria-hidden="true" /> Your own link, chosen and locked.
      </p>
    );
  }

  if (!editing) {
    return (
      <p className="hosted-link-note">
        Prefer a link with your name in it?{" "}
        <button type="button" className="link-btn" onClick={() => setEditing(true)}>
          <PencilLine size={13} aria-hidden="true" /> Choose your own link
        </button>
      </p>
    );
  }

  return (
    <div className="hosted-link-edit">
      <label htmlFor="hosted-link-input" className="hosted-label">
        Your own link
      </label>
      <div className="hosted-link-input">
        <span className="hosted-link-base">{base.replace(/^https:\/\//, "")}</span>
        <input
          id="hosted-link-input"
          className="input"
          value={value}
          onChange={(e) => setValue(tidy(e.target.value))}
          placeholder="your-company-name"
          autoFocus
          spellCheck={false}
          autoComplete="off"
          aria-describedby="hosted-link-status"
        />
      </div>
      <p id="hosted-link-status" className={`hosted-link-status is-${check.state}`} aria-live="polite">
        {check.state === "checking" && "Checking…"}
        {check.state === "ok" && (
          <>
            <Check size={14} aria-hidden="true" /> Available
          </>
        )}
        {check.state === "bad" && (
          <>
            <X size={14} aria-hidden="true" /> {check.reason}
          </>
        )}
        {check.state === "idle" && "Letters, numbers and hyphens, e.g. smith-heating. You can only choose once."}
      </p>
      {error && <p className="price-error">{error}</p>}
      <div className="hosted-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setEditing(false);
            setValue("");
            setError(null);
          }}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={check.state !== "ok"} onClick={() => setConfirming(true)}>
          <Lock size={16} aria-hidden="true" /> Save and lock link
        </button>
      </div>

      {/* The choice is permanent, so say so before saving */}
      <ConfirmDialog
        open={confirming}
        title="Lock in this link?"
        confirmLabel="Save and lock link"
        busy={saving}
        danger={false}
        onConfirm={() => void save()}
        onCancel={() => setConfirming(false)}
      >
        <p>
          Your quote page will be at <strong>{base + value}</strong>.
        </p>
        <p>This can't be undone, and the link can't be changed again later.</p>
        <p>
          Your current link <strong>{url}</strong> and its QR code will keep opening the same page. Download the new QR code for anything
          you print from now on.
        </p>
      </ConfirmDialog>
    </div>
  );
}
